import { useMemo } from 'react';

import { useDraftDefinition } from './contexts/DraftDefinitionProvider';
import { useDraftProgress } from './contexts/DraftProgressProvider';
import { useDraftStepProgress } from './hooks/useDraftStepProgress';
import { ManualPayoutPreviewField } from './ManualPayoutPreviewField';
import {
  enhanceFieldPreview,
  textFieldPreview
} from './utils/fieldPreviewEnhancer';
import { formatStepTitle } from './utils/formatters';
import type { DraftSteps } from './utils/types';

export const PayoutDraftPreview = () => {
  const { data } = useDraftProgress();
  const { fieldDefinitions } = useDraftDefinition();
  const { findStepIndex } = useDraftStepProgress();

  /**
   * Merge comleted steps with step preview
   */
  const enhanced = useMemo(() => {
    {
      const progressedSteps = Object.keys(data);
      const enhancedProgress = Object.entries(data).map(([step, payload]) => ({
        step: step as DraftSteps,
        title: formatStepTitle(step as DraftSteps),
        fields: Object.entries(payload).map(([field, value]) =>
          enhanceFieldPreview(field, value as string)
        )
      }));

      const uncompletedSteps = Object.entries(fieldDefinitions)
        .filter(([_step, payload]) => Object.keys(payload).length > 0)
        .filter(([step]) => progressedSteps.includes(step) === false)
        .map(([step, payload]) => ({
          step: step as DraftSteps,
          title: formatStepTitle(step as DraftSteps),
          fields: Object.entries(payload).map(([field]) =>
            textFieldPreview(field)
          )
        }));

      return [...enhancedProgress, ...uncompletedSteps].sort(
        (a, z) => findStepIndex(a.step) - findStepIndex(z.step)
      );
    }
  }, [data, fieldDefinitions, findStepIndex]);

  return (
    <div className="space-y-3 px-6 pb-6">
      {enhanced.map((step) => {
        return (
          <div key={step.title} className="space-y-2 border-b pb-3">
            <h2>{step.title}</h2>
            <div className="grid grid-cols-2 gap-y-2">
              {step.fields.map((field, index) => {
                return (
                  <ManualPayoutPreviewField
                    field={field}
                    displayEnd={index % 2 === 1}
                    key={`${step.title}.${field.label}`}
                    className="font-medium"
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
